import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { GlassFill } from '../../v10/ui';

// ---------------------------------------------------------------------------
// NEMOS Records — native replica of src/app/nemos/records/page.tsx.
// Same `hrmez_decisions` / `hrmez_ambiguous` / `hrmez_reports` tables
// (Supabase-direct, ported 1:1) for the Move Log / Review / Run History
// tabs.
//
// Web-only remainder: the "Index" tab lists the connected Google Drive tree
// via the server route /api/hrmez/drive (list action), which in turn needs
// a stored Google OAuth token (hrmez_drive_tokens). That server route is
// reachable from mobile (same EXPO_PUBLIC_API_BASE_URL used elsewhere), so
// it IS ported here — but if the account has no Drive connected (the
// common case, since Drive connect is a web-only OAuth flow), the tab will
// correctly show "No files found. Connect Google Drive first." exactly like
// web does in the same situation.
// ---------------------------------------------------------------------------

type TabKey = 'index' | 'log' | 'review' | 'reports';

interface StorageFile {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  modifiedTime?: string;
}

interface HrmezDecision {
  id: string;
  filename: string;
  source_path: string;
  destination_path: string;
  reason: string | null;
  method: 'rule' | 'ai' | 'user_override';
  confidence_score: number | null;
  timestamp: string;
}

interface HrmezAmbiguous {
  id: string;
  file_id: string;
  filename: string;
  source_path: string;
  ai_suggestion: string;
  ai_reasoning: string;
  confidence_score: number | null;
  status: 'pending' | 'approved' | 'overridden';
}

interface HrmezReport {
  id: string;
  run_at: string;
  files_processed: number;
  files_moved: number;
  files_flagged: number;
}

function formatTimestamp(ts: string) {
  const d = new Date(ts);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export function NemosRecords({ userId }: { userId: string }) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  const [tab, setTab] = useState<TabKey>('log');

  const [indexFiles, setIndexFiles] = useState<StorageFile[]>([]);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexError, setIndexError] = useState<string | null>(null);

  const [decisions, setDecisions] = useState<HrmezDecision[]>([]);
  const [decisionsLoading, setDecisionsLoading] = useState(false);

  const [ambiguous, setAmbiguous] = useState<HrmezAmbiguous[]>([]);
  const [ambiguousLoading, setAmbiguousLoading] = useState(false);
  const [reviewItem, setReviewItem] = useState<HrmezAmbiguous | null>(null);
  const [overridePath, setOverridePath] = useState('');

  const [reports, setReports] = useState<HrmezReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const driveAction = useCallback(
    async (action: string, params: Record<string, any> = {}) => {
      const apiBase = process.env.EXPO_PUBLIC_API_BASE_URL;
      if (!apiBase) throw new Error('Missing EXPO_PUBLIC_API_BASE_URL');
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Not signed in');
      const res = await fetch(`${apiBase}/api/hrmez/drive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ userId, action, ...params }),
      });
      if (!res.ok) {
        let msg = 'Drive operation failed';
        try {
          const err = await res.json();
          msg = err.error || msg;
        } catch {
          // non-JSON error body
        }
        throw new Error(msg);
      }
      return res.json();
    },
    [userId]
  );

  const loadIndex = useCallback(async () => {
    setIndexLoading(true);
    setIndexError(null);
    try {
      const data = await driveAction('list', { folderId: 'root' });
      if (data?.files) setIndexFiles(data.files);
    } catch (err: any) {
      setIndexError(err?.message || 'Failed to load index');
    } finally {
      setIndexLoading(false);
    }
  }, [driveAction]);

  const loadDecisions = useCallback(async () => {
    setDecisionsLoading(true);
    const { data } = await supabase.from('hrmez_decisions').select('*').eq('user_id', userId).order('timestamp', { ascending: false }).range(0, 49);
    if (data) setDecisions(data as HrmezDecision[]);
    setDecisionsLoading(false);
  }, [userId]);

  const loadAmbiguous = useCallback(async () => {
    setAmbiguousLoading(true);
    const { data } = await supabase.from('hrmez_ambiguous').select('*').eq('user_id', userId).order('created_at', { ascending: false });
    if (data) setAmbiguous(data as HrmezAmbiguous[]);
    setAmbiguousLoading(false);
  }, [userId]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    const { data } = await supabase.from('hrmez_reports').select('*').eq('user_id', userId).order('run_at', { ascending: false }).limit(50);
    if (data) setReports(data as HrmezReport[]);
    setReportsLoading(false);
  }, [userId]);

  useEffect(() => {
    loadIndex();
    loadDecisions();
    loadAmbiguous();
    loadReports();
  }, [loadIndex, loadDecisions, loadAmbiguous, loadReports]);

  const handleApproveAmbiguous = async (item: HrmezAmbiguous) => {
    try {
      await driveAction('move', { fileId: item.file_id, newParentId: item.ai_suggestion });
      await supabase.from('hrmez_ambiguous').update({ status: 'approved' }).eq('id', item.id);
      await supabase.from('hrmez_decisions').insert({
        user_id: userId,
        filename: item.filename,
        source_path: item.source_path,
        destination_path: item.ai_suggestion,
        reason: `Approved AI suggestion: ${item.ai_reasoning}`,
        method: 'user_override',
        confidence_score: 1.0,
      });
      loadAmbiguous();
      loadDecisions();
    } catch (err: any) {
      Alert.alert('Error', err?.message || 'Failed to approve');
    }
  };

  const handleOverrideAmbiguous = async () => {
    if (!reviewItem || !overridePath.trim()) return;
    try {
      await driveAction('move', { fileId: reviewItem.file_id, newParentId: overridePath });
      await supabase.from('hrmez_ambiguous').update({ status: 'overridden', override_path: overridePath }).eq('id', reviewItem.id);
      await supabase.from('hrmez_decisions').insert({
        user_id: userId,
        filename: reviewItem.filename,
        source_path: reviewItem.source_path,
        destination_path: overridePath,
        reason: `User override. AI suggested: ${reviewItem.ai_suggestion}`,
        method: 'user_override',
        confidence_score: 1.0,
      });
      setReviewItem(null);
      setOverridePath('');
      loadAmbiguous();
      loadDecisions();
    } catch (err: any) {
      Alert.alert('Error', err?.message || 'Failed to override');
    }
  };

  const pendingCount = ambiguous.filter((a) => a.status === 'pending').length;

  const TABS: { key: TabKey; label: string; badge?: number }[] = [
    { key: 'index', label: 'Index' },
    { key: 'log', label: 'Move Log' },
    { key: 'review', label: 'Review', badge: pendingCount },
    { key: 'reports', label: 'Run History' },
  ];

  return (
    <View style={styles.root}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabBarScroll}>
        <View style={styles.tabBar}>
          {TABS.map((t) => (
            <Pressable key={t.key} style={[styles.tabBtn, tab === t.key && styles.tabBtnActive]} onPress={() => setTab(t.key)}>
              <Text style={[styles.tabBtnText, tab === t.key && styles.tabBtnTextActive]}>{t.label}</Text>
              {!!t.badge && (
                <View style={styles.tabBadge}>
                  <Text style={styles.tabBadgeText}>{t.badge}</Text>
                </View>
              )}
            </Pressable>
          ))}
        </View>
      </ScrollView>

      {tab === 'index' && (
        <ScrollView style={styles.tabContent}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowHeaderText}>Google Drive Index</Text>
            <Pressable onPress={loadIndex}><Text style={styles.refreshText}>↻</Text></Pressable>
          </View>
          {indexLoading ? (
            <ActivityIndicator color={t.goldBright} style={{ marginTop: 16 }} />
          ) : indexError ? (
            <Text style={styles.errorText}>{indexError}</Text>
          ) : indexFiles.length === 0 ? (
            <Text style={styles.emptyText}>No files found. Connect Google Drive first (web only).</Text>
          ) : (
            indexFiles.map((file) => (
              <View key={file.id} style={styles.indexRow}>
                <Text style={styles.indexIcon}>{file.isFolder ? '📁' : '📄'}</Text>
                <Text style={styles.indexName} numberOfLines={1}>{file.name}</Text>
                <Text style={styles.indexMeta}>{file.isFolder ? 'Folder' : file.mimeType || '-'}</Text>
              </View>
            ))
          )}
        </ScrollView>
      )}

      {tab === 'log' && (
        <ScrollView style={styles.tabContent}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowHeaderText}>Move Log</Text>
            <Pressable onPress={loadDecisions}><Text style={styles.refreshText}>↻</Text></Pressable>
          </View>
          {decisionsLoading ? (
            <ActivityIndicator color={t.goldBright} style={{ marginTop: 16 }} />
          ) : decisions.length === 0 ? (
            <Text style={styles.emptyText}>No moves recorded yet.</Text>
          ) : (
            decisions.map((d) => (
              <View key={d.id} style={styles.decisionCard}>
                <GlassFill />
                <GlassFill />
                <View style={styles.decisionHeaderRow}>
                  <Text style={styles.decisionFilename} numberOfLines={1}>{d.filename}</Text>
                  <View style={[styles.methodBadge, d.method === 'rule' ? styles.methodRule : d.method === 'ai' ? styles.methodAi : styles.methodOverride]}>
                    <Text style={styles.methodBadgeText}>{d.method}</Text>
                  </View>
                </View>
                <Text style={styles.decisionPath} numberOfLines={1}>{d.source_path} → {d.destination_path}</Text>
                {!!d.reason && <Text style={styles.decisionReason} numberOfLines={2}>{d.reason}</Text>}
                <Text style={styles.decisionMeta}>
                  {formatTimestamp(d.timestamp)}
                  {d.confidence_score != null ? ` · ${Math.round(d.confidence_score * 100)}%` : ''}
                </Text>
              </View>
            ))
          )}
        </ScrollView>
      )}

      {tab === 'review' && (
        <ScrollView style={styles.tabContent}>
          {ambiguousLoading ? (
            <ActivityIndicator color={t.goldBright} style={{ marginTop: 16 }} />
          ) : ambiguous.length === 0 ? (
            <Text style={styles.emptyText}>No files awaiting review.</Text>
          ) : (
            ambiguous.map((item) => (
              <View key={item.id} style={[styles.decisionCard, item.status !== 'pending' && styles.decisionCardDim]}>
                <GlassFill />
                <GlassFill />
                <View style={styles.decisionHeaderRow}>
                  <Text style={styles.decisionFilename} numberOfLines={1}>{item.filename}</Text>
                  <View style={[styles.methodBadge, item.status === 'pending' ? styles.statusPending : item.status === 'approved' ? styles.statusApproved : styles.methodOverride]}>
                    <Text style={styles.methodBadgeText}>{item.status}</Text>
                  </View>
                </View>
                <Text style={styles.decisionReason}>From: {item.source_path}</Text>
                <View style={styles.aiBox}>
                  <Text style={styles.aiBoxText}>AI suggests: {item.ai_suggestion}</Text>
                  <Text style={styles.aiBoxText}>Reasoning: {item.ai_reasoning}</Text>
                  {item.confidence_score != null && <Text style={styles.aiBoxText}>Confidence: {Math.round(item.confidence_score * 100)}%</Text>}
                </View>
                {item.status === 'pending' && (
                  <View style={styles.reviewActions}>
                    <Pressable style={styles.reviewBtn} onPress={() => handleApproveAmbiguous(item)}>
                      <Text style={styles.reviewBtnText}>Accept</Text>
                    </Pressable>
                    <Pressable
                      style={styles.reviewBtn}
                      onPress={() => {
                        setReviewItem(item);
                        setOverridePath('');
                      }}
                    >
                      <Text style={styles.reviewBtnText}>Override</Text>
                    </Pressable>
                  </View>
                )}
              </View>
            ))
          )}
        </ScrollView>
      )}

      {tab === 'reports' && (
        <ScrollView style={styles.tabContent}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowHeaderText}>Run History</Text>
          </View>
          {reportsLoading ? (
            <ActivityIndicator color={t.goldBright} style={{ marginTop: 16 }} />
          ) : reports.length === 0 ? (
            <Text style={styles.emptyText}>No runs recorded yet.</Text>
          ) : (
            reports.map((r) => (
              <View key={r.id} style={styles.reportRow}>
                <GlassFill />
                <GlassFill />
                <Text style={styles.reportTime}>{formatTimestamp(r.run_at)}</Text>
                <Text style={styles.reportStat}>Processed: {r.files_processed}</Text>
                <Text style={[styles.reportStat, { color: t.onPace }]}>Moved: {r.files_moved}</Text>
                <Text style={[styles.reportStat, r.files_flagged > 0 && { color: t.behind }]}>Flagged: {r.files_flagged}</Text>
              </View>
            ))
          )}
        </ScrollView>
      )}

      <Modal visible={!!reviewItem} transparent animationType="fade" onRequestClose={() => setReviewItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>Override destination for &quot;{reviewItem?.filename}&quot;</Text>
            <Text style={styles.modalBody}>AI suggested: {reviewItem?.ai_suggestion}{'\n'}Enter the correct destination path or folder ID.</Text>
            <TextInput style={styles.modalInput} value={overridePath} onChangeText={setOverridePath} placeholder="e.g. 5 Econ/5R Resources" placeholderTextColor={t.ivorySoft} />
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setReviewItem(null)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnPrimary]} onPress={handleOverrideAmbiguous} disabled={!overridePath.trim()}>
                <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>Move Here</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu, fonts, radii }) =>
  StyleSheet.create({
  root: { flex: 1, gap: 8 },
  tabBarScroll: { flexGrow: 0 },
  tabBar: { flexDirection: 'row', gap: 6 },
  tabBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 7, borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  tabBtnActive: { backgroundColor: t.gold, ...neu.glowPrimary },
  tabBtnText: { fontSize: 12, fontWeight: '800', color: t.ivorySoft },
  tabBtnTextActive: { color: t.onGold },
  tabBadge: { backgroundColor: c.danger, borderRadius: 8, minWidth: 16, height: 16, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 3 },
  tabBadgeText: { fontSize: 9, fontWeight: '900', color: c.white },
  tabContent: { flex: 1 },
  rowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 4 },
  rowHeaderText: { fontSize: 14, fontWeight: '800', color: t.ivory },
  refreshText: { fontSize: 16, color: t.goldBright },
  errorText: { color: c.danger, fontSize: 13, marginTop: 12 },
  emptyText: { fontSize: 13, color: t.ivorySoft, textAlign: 'center', marginTop: 24 },

  indexRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: t.lineFaint },
  indexIcon: { fontSize: 15 },
  indexName: { flex: 1, fontSize: 13, color: t.ivory },
  indexMeta: { fontSize: 11, color: t.ivorySoft },

  decisionCard: { overflow: 'hidden', backgroundColor: 'transparent', borderRadius: radii.tile, padding: 10, gap: 4, marginBottom: 8, borderWidth: 1, borderColor: t.navyLine },
  decisionCardDim: { opacity: 0.6 },
  decisionHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  decisionFilename: { flex: 1, fontSize: 13, fontWeight: '800', color: t.ivory },
  decisionPath: { fontSize: 11, color: t.ivorySoft, fontFamily: 'Courier' },
  decisionReason: { fontSize: 12, color: t.ivory },
  decisionMeta: { fontSize: 11, color: t.ivorySoft },
  methodBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: radii.pill },
  methodBadgeText: { fontFamily: fonts.title, fontSize: 9, fontWeight: '800', color: c.white, textTransform: 'uppercase' },
  methodRule: { backgroundColor: c.blue },
  methodAi: { backgroundColor: c.purple },
  methodOverride: { backgroundColor: t.ivorySoft },
  statusPending: { backgroundColor: c.danger },
  statusApproved: { backgroundColor: t.onPace },

  aiBox: { backgroundColor: t.glass, borderRadius: radii.tile, padding: 8, gap: 2, borderWidth: 1, borderColor: t.navyLine },
  aiBoxText: { fontSize: 11, color: t.ivory },
  reviewActions: { flexDirection: 'row', gap: 8, marginTop: 4 },
  reviewBtn: { flex: 1, alignItems: 'center', paddingVertical: 7, borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  reviewBtnText: { fontSize: 12, fontWeight: '800', color: t.goldBright },

  reportRow: { overflow: 'hidden', backgroundColor: 'transparent', borderRadius: radii.tile, padding: 10, marginBottom: 6, gap: 2, borderWidth: 1, borderColor: t.navyLine },
  reportTime: { fontSize: 12, fontWeight: '800', color: t.ivory },
  reportStat: { fontSize: 12, color: t.ivorySoft },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modalCard: { backgroundColor: 'transparent', overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, borderRadius: radii.panel, padding: 18, gap: 12, width: '100%', maxWidth: 380, boxShadow: `0 10px 30px ${t.glowSoft}` as any },
  modalTitle: { fontSize: 16, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase', letterSpacing: 2.5 },
  modalBody: { fontSize: 13, color: t.ivorySoft, lineHeight: 18 },
  modalInput: { backgroundColor: c.inputFill, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14, color: t.ivory, borderWidth: 1, borderColor: t.navyLine },
  modalButtons: { flexDirection: 'row', gap: 10, justifyContent: 'flex-end' },
  modalBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radii.tile, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  modalBtnText: { fontWeight: '800', color: t.ivory },
  modalBtnPrimary: { backgroundColor: t.gold },
  modalBtnPrimaryText: { color: t.onGold },
}),
);
